/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   monitoredPipelinesApi.ts
 *     Feature         :   191-pipeline-monitoring-platform
 *     Description     :   API client for /api/monitored-pipelines/* — the registry-driven
 *                         monitoring read API. All requests use the centralized apiClient
 *                         for auth interceptors.
 *     Constitution    :   v1.18.0 | Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-11
 * =========================================================================================
 */

import { apiClient } from './apiClient';
import type {
  MonitoredPipelinesResponse,
  PipelineRunListFilters,
  PipelineRunListResponse,
  UnifiedRunListResponse,
  PipelineRunDetail,
  PipelineTrendResponse,
} from '@shared/types/monitoredPipelines';

const BASE = '/api/monitored-pipelines';

function buildRunQuery(params?: PipelineRunListFilters): URLSearchParams {
  const qs = new URLSearchParams();
  if (params?.status)   qs.set('status',   params.status);
  if (params?.instance) qs.set('instance', params.instance);
  if (params?.fromDate) qs.set('from',     params.fromDate);
  if (params?.toDate)   qs.set('to',       params.toDate);
  if (params?.page)     qs.set('page',     String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params?.sortBy)   qs.set('sortBy',   params.sortBy);
  if (params?.sortDir)  qs.set('sortDir',  params.sortDir);
  return qs;
}

/** GET /api/monitored-pipelines — registry + health, domain-grouped. */
export async function fetchPipelineOverview(): Promise<MonitoredPipelinesResponse> {
  const { data } = await apiClient.get<MonitoredPipelinesResponse>(BASE);
  return data;
}

export interface UnifiedRunListParams extends PipelineRunListFilters {
  /** Restrict the union to these registry keys; omit = all present pipelines. */
  keys?: string[];
}

/** GET /api/monitored-pipelines/runs — unified cross-process explorer. */
export async function fetchUnifiedRuns(params?: UnifiedRunListParams): Promise<UnifiedRunListResponse> {
  const qs = buildRunQuery(params);
  if (params?.keys?.length) qs.set('keys', params.keys.join(','));
  const query = qs.toString();
  const { data } = await apiClient.get<UnifiedRunListResponse>(`${BASE}/runs${query ? `?${query}` : ''}`);
  return data;
}

/** GET /api/monitored-pipelines/:key/runs — per-process paginated runs. */
export async function fetchPipelineRuns(
  key: string,
  params?: PipelineRunListFilters,
): Promise<PipelineRunListResponse> {
  const query = buildRunQuery(params).toString();
  const { data } = await apiClient.get<PipelineRunListResponse>(
    `${BASE}/${encodeURIComponent(key)}/runs${query ? `?${query}` : ''}`,
  );
  return data;
}

/** GET /api/monitored-pipelines/:key/trend — duration trend + anomaly flags. */
export async function fetchPipelineTrend(
  key: string,
  days: number,
  instance?: string,
): Promise<PipelineTrendResponse> {
  const qs = new URLSearchParams({ days: String(days) });
  if (instance) qs.set('instance', instance);
  const { data } = await apiClient.get<PipelineTrendResponse>(
    `${BASE}/${encodeURIComponent(key)}/trend?${qs.toString()}`,
  );
  return data;
}

/** GET /api/monitored-pipelines/:key/runs/:batchId — full run detail. */
export async function fetchPipelineRunDetail(key: string, batchId: string): Promise<PipelineRunDetail> {
  const { data } = await apiClient.get<PipelineRunDetail>(
    `${BASE}/${encodeURIComponent(key)}/runs/${encodeURIComponent(batchId)}`,
  );
  return data;
}

/** GET /api/settings/feature-flags/pipeline_monitoring_v2_enabled — page gate (T017). */
export async function fetchPipelineMonitoringV2Flag(): Promise<boolean> {
  const { data } = await apiClient.get<{ success: boolean; data: { key: string; value: boolean } }>(
    '/api/settings/feature-flags/pipeline_monitoring_v2_enabled',
  );
  return data?.data?.value === true;
}
